import { useTranslation } from "next-i18next";
import { useFollowUnderlyingMutation, useUnfollowUnderlyingMutation } from "src/api/client/watchlist.api";
import { ToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { useUnderlyingFollowStore } from "src/stores/uderlying-follow-store";
import { useUserStore } from "src/stores/user-store";
import { useLinkBuilder } from "../use-link-builder";

const SUCCESS_MESSAGE_DURATION = 5000;
export const useUnderlyingFollowing = ({ underlyingIsin, isUnderlyingIndex }: { underlyingIsin: string; isUnderlyingIndex?: boolean }) => {
    const { t } = useTranslation("common");
    const { setIsFollowedUnderlying, canBeFollowedUnderlyingIsins, followedUnderlyingIsins } = useUnderlyingFollowStore();
    const toast = useWfToast();
    const { isLoggedIn } = useUserStore();
    const { openLoginModal } = useLinkBuilder();

    const textFollowSuccess = isUnderlyingIndex ? t("toast.index-follow-success") : t("toast.underlying-follow-success");
    const textUnfollowSuccess = isUnderlyingIndex ? t("toast.index-unfollow-success") : t("toast.underlying-unfollow-success");

    const isFollowed = followedUnderlyingIsins.includes(underlyingIsin);
    const canBeFollowed = canBeFollowedUnderlyingIsins.includes(underlyingIsin);

    const onError = () => {
        toast({ status: ToastStatus.Error, title: t("general-error") });
        setIsFollowedUnderlying(underlyingIsin, !isFollowed);
    };

    const onSuccess = (message: string) => () => {
        toast({
            duration: SUCCESS_MESSAGE_DURATION,
            status: ToastStatus.Success,
            title: message,
        });
    };

    const followUnderlyingMutation = useFollowUnderlyingMutation({ onError, onSuccess: onSuccess(textFollowSuccess) });
    const unfollowUnderlyingMutation = useUnfollowUnderlyingMutation(underlyingIsin, {
        onError,
        onSuccess: onSuccess(textUnfollowSuccess),
    });

    const toggleIsFollowed = () => {
        if (!isLoggedIn) {
            void openLoginModal();
            return;
        }

        setIsFollowedUnderlying(underlyingIsin, !isFollowed);

        if (isFollowed) {
            unfollowUnderlyingMutation.mutate();
        } else {
            followUnderlyingMutation.mutate({ isin: underlyingIsin });
        }
    };

    return {
        canBeFollowed,
        isFollowed,
        toggleIsFollowed,
    };
};
